import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiClient } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { loadFeatureFlags } from '../config/featureFlags';
import { Dataset } from '../types';
import { Campaign } from '../types/campaign';

interface CreditUsageData {
  current_month_credits: number;
}

interface SubscriptionData {
  is_active: boolean;
  plan_id: string | null;
  plan_name: string | null;
  expires_at: string | null;
}


interface AppDataContextType {
  // Credits
  creditData: CreditUsageData | null;
  isLoadingCredits: boolean;
  refreshCredits: () => Promise<void>;

  // Subscription
  subscriptionData: SubscriptionData | null;
  isLoadingSubscription: boolean;

  // Datasets
  datasets: Dataset[] | null;
  isLoadingDatasets: boolean;
  isLoadingMore: boolean;
  hasMoreDatasets: boolean;
  refreshDatasets: () => Promise<void>;
  loadMoreDatasets: () => Promise<void>;
  addDataset: (dataset: Dataset) => void;
  removeDataset: (datasetId: string) => void;
  updateDataset: (datasetId: string, updates: Partial<Dataset>) => void;

  // Campaigns (for sidebar - cached to avoid refetching on navigation)
  campaigns: Campaign[];
  isLoadingCampaigns: boolean;
  hasMoreCampaigns: boolean;
  refreshCampaigns: () => Promise<void>;
  loadMoreCampaigns: () => Promise<void>;
  addCampaign: (campaign: Campaign) => void;
  updateCampaign: (campaignId: string, updates: Partial<Campaign>) => void;
  removeCampaign: (campaignId: string) => void;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const AppDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [creditData, setCreditData] = useState<CreditUsageData | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [datasets, setDatasets] = useState<Dataset[] | null>(null);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreDatasets, setHasMoreDatasets] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [hasMoreCampaigns, setHasMoreCampaigns] = useState(false);
  const [campaignOffset, setCampaignOffset] = useState(0);

  const refreshCredits = useCallback(async () => {
    try {
      const data = await apiClient.getCredits();
      setCreditData({
        current_month_credits: data.credits
      });
    } catch (err) {
      console.error('Error loading credit usage:', err);
    }
  }, []);

  const loadCredits = useCallback(async () => {
    setIsLoadingCredits(true);
    await refreshCredits();
    setIsLoadingCredits(false);
  }, [refreshCredits]);

  const loadSubscription = useCallback(async () => {
    try {
      setIsLoadingSubscription(true);
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionData(data);
    } catch (err) {
      console.error('Error loading subscription:', err);
    } finally {
      setIsLoadingSubscription(false);
    }
  }, []);

  const CAMPAIGNS_PAGE_SIZE = 10;

  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoadingCampaigns(true);
      const response = await apiClient.listCampaigns({ limit: CAMPAIGNS_PAGE_SIZE, offset: 0 });
      const fetched = response.campaigns || [];
      // Sort by created_at descending (most recent first)
      const sortedCampaigns = fetched.sort((a: Campaign, b: Campaign) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setCampaigns(sortedCampaigns);
      setCampaignOffset(sortedCampaigns.length);
      setHasMoreCampaigns(fetched.length >= CAMPAIGNS_PAGE_SIZE);
    } catch {
      // Silently fail - campaigns feature might not be set up yet
      setCampaigns([]);
      setHasMoreCampaigns(false);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, []);

  const refreshCampaigns = useCallback(async () => {
    await loadCampaigns();
  }, [loadCampaigns]);

  const loadMoreCampaigns = useCallback(async () => {
    if (!hasMoreCampaigns || isLoadingCampaigns) return;
    setIsLoadingCampaigns(true);
    try {
      const response = await apiClient.listCampaigns({ limit: CAMPAIGNS_PAGE_SIZE, offset: campaignOffset });
      const fetched = response.campaigns || [];
      const sorted = fetched.sort((a: Campaign, b: Campaign) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
      setCampaigns(prev => [...prev, ...sorted]);
      setCampaignOffset(prev => prev + sorted.length);
      setHasMoreCampaigns(fetched.length >= CAMPAIGNS_PAGE_SIZE);
    } catch {
      // Silently fail
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [hasMoreCampaigns, isLoadingCampaigns, campaignOffset]);

  const addCampaign = useCallback((campaign: Campaign) => {
    setCampaigns(prev => [campaign, ...prev]);
    setCampaignOffset(prev => prev + 1);
  }, []);

  const updateCampaignInList = useCallback((campaignId: string, updates: Partial<Campaign>) => {
    setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, ...updates } : c));
  }, []);

  const removeCampaign = useCallback((campaignId: string) => {
    setCampaigns(prev => prev.filter(c => c.id !== campaignId));
  }, []);

  const loadDatasets = async (reset: boolean = false) => {
    try {
      if (reset) {
        setIsLoadingDatasets(true);
      } else {
        setIsLoadingMore(true);
      }

      const offset = reset ? 0 : currentOffset;
      const limit = reset ? 20 : 10;
      const result = await apiClient.getLeadTablesPaginated(limit, offset);

      if (reset) {
        setDatasets(result.tables);
        setCurrentOffset(result.tables.length);
      } else {
        setDatasets(prev => prev ? [...prev, ...result.tables] : result.tables);
        setCurrentOffset(prev => prev + result.tables.length);
      }

      setHasMoreDatasets(result.hasMore);
    } catch (err) {
      console.error('Error loading datasets:', err);
    } finally {
      setIsLoadingDatasets(false);
      setIsLoadingMore(false);
    }
  };

  const refreshDatasets = async () => {
    setCurrentOffset(0);
    await loadDatasets(true);
  };

  const loadMoreDatasets = async () => {
    if (hasMoreDatasets && !isLoadingDatasets && !isLoadingMore) {
      await loadDatasets(false);
    }
  };

  const addDataset = (dataset: Dataset) => {
    setDatasets(prev => prev ? [dataset, ...prev] : [dataset]);
    setCurrentOffset(prev => prev + 1);
  };

  const removeDataset = (datasetId: string) => {
    setDatasets(prev => prev ? prev.filter(d => d.id !== datasetId) : null);
  };

  const updateDataset = (datasetId: string, updates: Partial<Dataset>) => {
    setDatasets(prev =>
      prev ? prev.map(d => d.id === datasetId ? { ...d, ...updates } : d) : null
    );
  };

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      loadCredits();
      loadSubscription();
      loadDatasets(true);
      loadCampaigns();
      // Load feature flags when user is authenticated
      loadFeatureFlags();
    } else if (!isAuthenticated && !authLoading) {
      setCreditData(null);
      setIsLoadingCredits(false);
      setSubscriptionData(null);
      setIsLoadingSubscription(false);
      setDatasets(null);
      setIsLoadingDatasets(false);
      setIsLoadingMore(false);
      setHasMoreDatasets(false);
      setCurrentOffset(0);
      setCampaigns([]);
      setIsLoadingCampaigns(false);
      setHasMoreCampaigns(false);
      setCampaignOffset(0);
    }
  }, [isAuthenticated, authLoading, loadCredits, loadSubscription, loadCampaigns]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const channel = supabase
      .channel('lead_tables_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lead_tables',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newDataset = payload.new as Dataset;
            setDatasets(prev => prev ? [newDataset, ...prev] : [newDataset]);
            setCurrentOffset(prev => prev + 1);
          } else if (payload.eventType === 'UPDATE') {
            setDatasets(prev =>
              prev ? prev.map(d => {
                if (d.id === (payload.new as Dataset).id) {
                  return {
                    ...(payload.new as Dataset),
                    lead_count: (payload.new as any).lead_count ?? d.lead_count
                  };
                }
                return d;
              }) : null
            );
          } else if (payload.eventType === 'DELETE') {
            setDatasets(prev =>
              prev ? prev.filter(d => d.id !== (payload.old as Dataset).id) : null
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    let lastRefresh = Date.now();
    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastRefresh < 30_000) return;
      lastRefresh = Date.now();
      refreshCredits();
    };
    const poll = window.setInterval(refreshIfVisible, 60_000);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [isAuthenticated, user?.id, refreshCredits]);

  const value: AppDataContextType = {
    creditData,
    isLoadingCredits,
    refreshCredits,
    subscriptionData,
    isLoadingSubscription,
    datasets,
    isLoadingDatasets,
    isLoadingMore,
    hasMoreDatasets,
    refreshDatasets,
    loadMoreDatasets,
    addDataset,
    removeDataset,
    updateDataset,
    campaigns,
    isLoadingCampaigns,
    hasMoreCampaigns,
    refreshCampaigns,
    loadMoreCampaigns,
    addCampaign,
    updateCampaign: updateCampaignInList,
    removeCampaign,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};